// helper methods for view transactions.
// create pagination model for the number of page buttons for the pagination layout.
// extracted out to pure javascript method to calculate and create the structure of the pagination
// and will be used by the pagination react UI component. Easier to unit test and maintain.

import {
  TransactionSearchResponse,
} from '../types'
import { CardTransaction } from '../Type/ViewTransaction'

const MAX_PAGE_ITEMS = 12

export type PaginationItem = { type: 'page'; value: number } | { type: 'ellipsis'; value: '...' }

export function createPaginationModel (
  totalPages: number,
  currentPage: number,
): PaginationItem[] {
  const items: PaginationItem[] = []

  // if total pages are equal to MAX or less, just show them all without ellipses
  if (totalPages <= MAX_PAGE_ITEMS) {
    for (let i = 1; i <= totalPages; i++) {
      items.push({ type: 'page', value: i })
    }
    return items
  }

  // scenario where current selected page will cause 2 ellipses to be visible
  const showLeadingEllipsis = currentPage >= 8

  if (showLeadingEllipsis) { // Build Ex. [1, 2, '...',  5, 6, 7, 8, 9, 10, 11, '...', 98, 99]
    items.push({ type: 'page', value: 1 })
    items.push({ type: 'page', value: 2 })
    items.push({ type: 'ellipsis', value: '...' })

    // determine sliding window of pages for user selection up to first ellipses
    let startPage = currentPage - 3
    let endPage = currentPage + 3

    if (endPage >= totalPages - 2) {
      endPage = totalPages - 2
      startPage = Math.max(3, endPage - 6)
    }

    // Build out rest of pages
    for (let i = startPage; i <= endPage; i++) {
      items.push({ type: 'page', value: i })
    }

    // last ellipses and last to pages
    if (endPage < totalPages - 2) {
      items.push({ type: 'ellipsis', value: '...' })
    }
  } else { // Build Ex. [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, '...', 98, 99] }
    const renderEnd = currentPage + 3
    for (let i = 1; i <= renderEnd; i++) {
      items.push({ type: 'page', value: i })
    }
    items.push({ type: 'ellipsis', value: '...' })
  }

  // create last 2 pages always after last ellipses
  items.push({ type: 'page', value: totalPages - 1 })
  items.push({ type: 'page', value: totalPages })

  return items
}

export type SortDirection = 'asc' | 'desc' | 'unsorted'

export interface SortConfig {
  column: string
  direction: SortDirection
}

export function getPagedTransactions (
  transactions: CardTransaction[], currentPage: number, itemsPerPage: number
): CardTransaction[] {
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const visibleTransactions: CardTransaction[] = transactions.slice(indexOfFirstItem, indexOfLastItem)
  return visibleTransactions
}

export function getSortedTransactions (
  transactions: CardTransaction[], sortConfig: SortConfig | null
): CardTransaction[] {
  const sortedRecords = [...transactions] // copy records
  if (sortConfig) {
    const { column, direction } = sortConfig
    sortedRecords.sort((a, b) => {
      const valueA = (a as any)[column]
      const valueB = (b as any)[column]

      if (valueA < valueB) {
        return direction === 'asc' ? -1 : 1
      }

      if (valueA > valueB) {
        return direction === 'asc' ? 1 : -1
      }

      return 0
    })
  }
  return sortedRecords
}

interface Transaction {
  masked_card_number: string;
  card_brand: string;
  process_method: string;
  [key: string]: any
}

export function parseTransactionSearchResponse (json: any): TransactionSearchResponse {
  const transactions = json.transactions?.map(({
    masked_card_number,
    card_brand,
    process_method,
    surcharge_card_type,
    card_type,
    ...rest
  }: Transaction) => ({
    ...rest,
    cardNumber: masked_card_number,
    cardBrand: card_brand,
    processMethod: process_method,
    card_type: surcharge_card_type !== undefined ? surcharge_card_type : card_type,
  }))
  return {
    summary: json.summary,
    cardSummaries: json.cardSummaries,
    transactions,
  }
}
